import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Paper, AppBar, Toolbar, IconButton, Tooltip,
  List, ListItem, ListItemText, Divider, Chip, Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import jsPDF from 'jspdf';
import api from '../api';

const ConsequencesReport = () => {
  const navigate = useNavigate();
  const { date } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReport = async () => {
      try {
        // Add timestamp to force fresh request
        const timestamp = Date.now();
        const response = await api.get(`/api/psychology/consequences-report/${date}?t=${timestamp}`);
        setReport(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch consequences report:', err);
        if (err.response?.status === 401) {
          setError('Authentication failed. Please refresh the page and try again.');
        } else {
          setError('Failed to load report: ' + (err.response?.data?.message || err.message));
        }
        setLoading(false);
      }
    };

    if (date) {
      fetchReport();
    }
  }, [date]);

  const downloadPDF = () => {
    if (!report) return;

    const doc = new jsPDF();
    
    // Set document properties
    doc.setFontSize(18);
    doc.text('Trading Consequences Report', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(`Date: ${report.date}`, 20, 30);
    doc.text(`Psychology Score: ${report.analysis.psychologyScore}/100`, 20, 40);
    doc.text(`Risk Level: ${report.analysis.riskLevel}`, 20, 50);
    doc.text(`Trading Recommendation: ${report.analysis.tradingAdvice}`, 20, 60);
    
    // Add detailed breakdown
    let yPos = 70;
    doc.setFontSize(14);
    doc.text('Detailed Breakdown:', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(12);
    doc.text(`Mood: ${report.analysis.mood || 'N/A'}`, 20, yPos);
    yPos += 10;
    doc.text(`Sleep Hours: ${report.analysis.sleepHours || 'N/A'}`, 20, yPos);
    yPos += 10;
    doc.text(`Stress Level (1-10): ${report.analysis.stressLevel || 'N/A'}`, 20, yPos);
    yPos += 10;
    doc.text(`Confidence Level (1-10): ${report.analysis.confidenceLevel || 'N/A'}`, 20, yPos);
    yPos += 10;
    doc.text(`Focus Level (1-10): ${report.analysis.focusLevel || 'N/A'}`, 20, yPos);
    yPos += 10;
    
    if (report.analysis.notes) {
      doc.text(`Notes: ${String(report.analysis.notes)}`, 20, yPos);
      yPos += 10;
    }
    
    // Add warnings if they exist
    if (report.analysis.warnings && report.analysis.warnings.length > 0) {
      yPos += 10;
      doc.setFontSize(14);
      doc.text('Warnings:', 20, yPos);
      yPos += 10;
      
      doc.setFontSize(12);
      report.analysis.warnings.forEach((warning, index) => {
        if (yPos > 280) { // Add new page if needed
          doc.addPage();
          yPos = 20;
        }
        doc.text(`- ${warning}`, 20, yPos);
        yPos += 10;
      });
    }
    
    // Add recommendations if they exist
    if (report.analysis.recommendations && report.analysis.recommendations.length > 0) {
      yPos += 10;
      doc.setFontSize(14);
      doc.text('Recommendations:', 20, yPos);
      yPos += 10;
      
      doc.setFontSize(12);
      report.analysis.recommendations.forEach((rec, index) => {
        if (yPos > 280) { // Add new page if needed
          doc.addPage();
          yPos = 20;
        }
        doc.text(`- ${rec}`, 20, yPos);
        yPos += 10;
      });
    }
    
    // Add consequences if they exist
    if (report.analysis.consequences) {
      yPos += 10;
      doc.setFontSize(14);
      doc.text('Consequences of Trading Against Advice:', 20, yPos);
      yPos += 10;
      
      doc.setFontSize(12);
      doc.text(`Trades Made: ${report.analysis.consequences.tradesCount}`, 20, yPos);
      yPos += 10;
      doc.text(`Total P&L: $${report.analysis.consequences.totalPnl.toFixed(2)}`, 20, yPos);
      yPos += 10;
      doc.text(`Win Rate: ${report.analysis.consequences.winRate.toFixed(1)}%`, 20, yPos);
      yPos += 10;
      doc.text(`Risk Taken: ${report.analysis.consequences.riskTaken.toFixed(1)}%`, 20, yPos);
      yPos += 10;
      doc.text(`Message: ${report.analysis.consequences.message}`, 20, yPos, { maxWidth: 170 });
    }
    
    // Add full text report
    yPos += 20;
    doc.setFontSize(14);
    doc.text('Full Report Text:', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(12);
    const splitText = doc.splitTextToSize(report.text, 170);
    doc.text(splitText, 20, yPos);
    
    // Save the PDF
    doc.save(`trading-consequences-report-${report.date}.pdf`);
  };

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />;
  
  if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;

  if (!report) return <Alert severity="info" sx={{ m: 4 }}>No report data available.</Alert>;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'linear-gradient(to bottom, #f5f5f5, #e0e0e0)', p: 4 }}>
      <AppBar position="sticky" sx={{ bgcolor: 'var(--primary-color)', mb: 4 }}>
        <Toolbar>
          <Tooltip title="Back to Dashboard">
            <IconButton color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Trading Consequences Report
          </Typography>
          <Button
            variant="contained"
            onClick={downloadPDF}
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
          >
            Download PDF
          </Button>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} sx={{ maxWidth: 900, mx: 'auto', p: 4, borderRadius: 4, bgcolor: '#fff' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', color: '#1976d2', mb: 4 }}>
          Consequences Report
        </Typography>
        
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>Report Details</Typography>
          <Typography><strong>Date:</strong> {report.date}</Typography>
          <Typography><strong>Psychology Score:</strong> {report.analysis.psychologyScore}/100</Typography>
          <Typography><strong>Risk Level:</strong> 
            <Chip 
              label={report.analysis.riskLevel} 
              color={
                report.analysis.riskLevel === 'CRITICAL' ? 'error' :
                report.analysis.riskLevel === 'HIGH' ? 'warning' :
                report.analysis.riskLevel === 'MEDIUM' ? 'info' : 'success'
              }
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
          <Typography><strong>Trading Recommendation:</strong> {report.analysis.tradingAdvice}</Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom sx={{ color: '#dc004e' }}>Detailed Breakdown</Typography>
        <List>
          <ListItem>
            <ListItemText primary="Mood" secondary={report.analysis.mood || 'N/A'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Sleep Hours" secondary={report.analysis.sleepHours || 'N/A'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Stress Level (1-10)" secondary={report.analysis.stressLevel || 'N/A'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Confidence Level (1-10)" secondary={report.analysis.confidenceLevel || 'N/A'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Focus Level (1-10)" secondary={report.analysis.focusLevel || 'N/A'} />
          </ListItem>
          {report.analysis.notes && (
            <ListItem>
              <ListItemText primary="Notes" secondary={String(report.analysis.notes)} />
            </ListItem>
          )}
        </List>

        <Divider sx={{ my: 3 }} />

        {report.analysis.warnings && report.analysis.warnings.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ff9800' }}>Warnings</Typography>
            <List>
              {report.analysis.warnings.map((warning, index) => (
                <ListItem key={index}>
                  <ListItemText primary={warning} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {report.analysis.recommendations && report.analysis.recommendations.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>Recommendations</Typography>
            <List>
              {report.analysis.recommendations.map((rec, index) => (
                <ListItem key={index}>
                  <ListItemText primary={rec} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {report.analysis.consequences && (
          <Box sx={{ mb: 4, p: 3, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#f44336' }}>Consequences of Trading Against Advice</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Trades Made" secondary={report.analysis.consequences.tradesCount} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Total P&L" secondary={`$${report.analysis.consequences.totalPnl.toFixed(2)}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Win Rate" secondary={`${report.analysis.consequences.winRate.toFixed(1)}%`} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Risk Taken" secondary={`${report.analysis.consequences.riskTaken.toFixed(1)}%`} />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Message" 
                  secondary={report.analysis.consequences.message} 
                  secondaryTypographyProps={{ style: { fontWeight: 'bold' } }}
                />
              </ListItem>
            </List>
          </Box>
        )}

        {report.decisionAnalysis && report.decisionAnalysis.losses > 0 && (
          <Box sx={{ mb: 4, p: 3, bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#9c27b0' }}>
              🧠 Psychological Decision Analysis
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
              Analysis of psychological factors that caused your trading losses
            </Typography>
            
            <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Trading Summary:</Typography>
              <Typography>• Total Trades: {report.decisionAnalysis.totalTrades}</Typography>
              <Typography>• Losing Trades: {report.decisionAnalysis.losses}</Typography>
              <Typography>• Average Loss: ${report.decisionAnalysis.avgLoss.toFixed(2)}</Typography>
            </Box>

            {report.decisionAnalysis.psychologicalFactors && report.decisionAnalysis.psychologicalFactors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                  🚨 Identified Psychological Factors Causing Losses:
                </Typography>
                {report.decisionAnalysis.psychologicalFactors.map((factor, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.9)', borderLeft: '4px solid #f44336', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                      {factor.factor} ({factor.severity} Impact)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>What happened:</strong> {factor.description}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Financial Impact:</strong> {factor.impact}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      <strong>How to Fix:</strong> {factor.recommendation}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {report.decisionAnalysis.rootCauses && report.decisionAnalysis.rootCauses.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: '#ff5722' }}>
                  🎯 Root Cause Analysis:
                </Typography>
                {report.decisionAnalysis.rootCauses.map((cause, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.9)', borderLeft: '4px solid #ff5722', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ff5722' }}>
                      {cause.cause}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Affected Factors:</strong> {cause.factors.join(', ')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Impact:</strong> {cause.impact}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      <strong>Solution:</strong> {cause.recommendation}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 4, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {report.text}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ConsequencesReport;